import { useCallback, useMemo, useState } from 'react';
import { useLabeling } from '../context/useLabeling';
import type { ImageLabel, ToolImage } from '../types/LabelingTypes';
import {
  answerDichotomous,
  currentIndex,
  initDichotomousSearch,
  sortByCapturedAt,
  type DichotomousConflict,
  type DichotomousPhase,
  type DichotomousState,
} from '../dichotomous/dichotomousSearch';
import {
  computeBulkPlan,
  resolveConflict as resolveConflictPure,
  type ConflictChoice,
  type DichotomousBulkPlan,
} from '../dichotomous/dichotomousPlan';
import {
  clearDichotomousPersisted,
  loadDichotomousPersisted,
  saveDichotomousPersisted,
} from '../storage/labelingStorage';

/**
 * React driver for the pure dichotomous search state machine.
 *
 * The hook owns exactly three things the pure modules cannot: the working set (the whole
 * experiment's images, capturedAt-sorted — every part shares the same physical tool, so the
 * cut point is a property of the tool, not of any one part), the persisted snapshot, and the
 * bulk write. Every state transition still goes through `dichotomousSearch` / `dichotomousPlan`
 * — nothing here recomputes a bracket or a plan on its own.
 */

/** Terminal phases where a bulk plan exists and may be shown to the annotator. */
const TERMINAL_PHASES: readonly DichotomousPhase[] = [
  'all-ok',
  'all-worn',
  'all-unreadable',
  'summary',
];

export interface DichotomousSearchApi {
  phase: DichotomousPhase;
  state: DichotomousState;
  /** Image the annotator must answer about, or `undefined` when the phase asks no question. */
  currentImage: ToolImage | undefined;
  /** 1-indexed position of the pending question within its phase family, and its bound. */
  progress: { n: number; total: number };
  /** `size` counts the images not yet excluded by the search, endpoints included. */
  bracket: { lo: number; hi: number; size: number };
  conflict: DichotomousConflict | null;
  answer(label: ImageLabel): void;
  resolveConflict(choice: ConflictChoice): void;
  confirmBulk(): Promise<void>;
  applyDegenerate(): Promise<void>;
  restart(): void;
  /** `null` outside a terminal phase — the summary screen renders the counts from this. */
  plan: DichotomousBulkPlan | null;
}

export function useDichotomousSearch(): DichotomousSearchApi {
  const { items, sessionId, applyLabelsBulk } = useLabeling();

  // The pure machine indexes `state.keys`, which is this list's key list — so the slice must be
  // derived exactly the way the persistence validator derives it, or a restored snapshot would
  // be rejected (or worse, accepted against a differently-ordered list). No part filter: every
  // part of the experiment shares the same tool, so they are searched together.
  const slice = useMemo(() => sortByCapturedAt(items), [items]);

  const [state, setState] = useState<DichotomousState>(
    () => loadDichotomousPersisted(sessionId, items) ?? initDichotomousSearch(slice)
  );

  /**
   * Single write path for every transition: persisting here — instead of in an effect on
   * `state` — guarantees the freshly hydrated snapshot is never immediately overwritten by the
   * mount render, which would turn a restore into a silent reset.
   */
  const commit = useCallback(
    (next: DichotomousState): void => {
      setState(next);
      saveDichotomousPersisted(sessionId, next);
    },
    [sessionId]
  );

  const answer = useCallback(
    (label: ImageLabel): void => {
      // Out-of-contract calls throw from `answerDichotomous`; swallowing that would hide a
      // caller bug behind a frozen UI.
      commit(answerDichotomous(state, label));
    },
    [commit, state]
  );

  const resolveConflict = useCallback(
    (choice: ConflictChoice): void => {
      commit(resolveConflictPure(state, choice));
    },
    [commit, state]
  );

  const restart = useCallback((): void => {
    commit(initDichotomousSearch(slice));
  }, [commit, slice]);

  /**
   * The one bulk-write implementation, exposed under two names because the summary screen and
   * the degenerate notice are two different UI moments with identical behaviour.
   */
  const applyPlan = useCallback(async (): Promise<void> => {
    const plan = computeBulkPlan(state);
    const entries = [
      ...plan.ok.map((key) => ({ key, label: 'ok' as const })),
      ...plan.worn.map((key) => ({ key, label: 'worn' as const })),
      ...plan.unreadable.map((key) => ({ key, label: 'unreadable' as const })),
    ];
    await applyLabelsBulk(entries);
    clearDichotomousPersisted(sessionId);
  }, [applyLabelsBulk, sessionId, state]);

  const index = currentIndex(state);
  const currentImage = useMemo(() => {
    if (index === null) return undefined;
    const key = state.keys[index];
    return slice.find((item) => item.key === key);
  }, [index, slice, state.keys]);

  const plan = useMemo(() => {
    if (!TERMINAL_PHASES.includes(state.phase)) return null;
    try {
      return computeBulkPlan(state);
    } catch {
      // A terminal phase with an unconverged bracket can only come from a corrupted snapshot;
      // the summary screen shows nothing rather than crashing the whole mode.
      return null;
    }
  }, [state]);

  return {
    phase: state.phase,
    state,
    currentImage,
    progress: computeProgress(state, index !== null),
    bracket: { lo: state.lo, hi: state.hi, size: state.hi - state.lo + 1 },
    conflict: state.conflict,
    answer,
    resolveConflict,
    confirmBulk: applyPlan,
    applyDegenerate: applyPlan,
    restart,
    plan,
  };
}

/**
 * Two counters, one shape. The search family counts every answer given so far against the
 * endpoint + narrowing budget; verification counts the drained part of its own queue against the
 * sample size frozen at `verifyTotal`. In both cases a *pending* question is counted as the one
 * being asked (`+ 1`), so the UI reads "Comparison 3 of 5" for the question on screen rather than
 * for the one already answered.
 */
function computeProgress(state: DichotomousState, pending: boolean): { n: number; total: number } {
  if (state.phase === 'verify') {
    const answered = state.verifyTotal - state.verifyQueue.length;
    return {
      n: Math.min(pending ? answered + 1 : state.verifyTotal, state.verifyTotal),
      total: state.verifyTotal,
    };
  }
  // `maxComparisons` bounds the narrowing steps only; the two endpoint confirmations are extra.
  const total = state.maxComparisons + 2;
  const n = pending ? state.comparisonCount + 1 : state.comparisonCount;
  return { n: Math.min(n, total), total };
}
